import { useState, useMemo } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Clock, ExternalLink, Layers, Pencil, Search } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { apiList, apiPatch } from "@/integrations/api/client";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { canRunDepreciation } from "@/lib/permissions";
import { useBranch } from "@/context/BranchContext";
import { inr } from "@/features/finance/format";

export const Route = createFileRoute("/_authenticated/finance/capitalisation")({
  component: CapitalisationPage,
});

export type CapitalisationRow = {
  id: string;
  asset_code: string;
  name: string;
  basic_cost: number | null;
  total_capitalised_value: number | null;
  capitalisation_date: string | null;
  capitalisation_status: string;
  capitalisation_remarks: string | null;
  department_name: string | null;
};

function usePendingRows(branchId: string | null) {
  return useQuery({
    queryKey: ["capitalisation-pending", branchId ?? null],
    queryFn: async () => {
      try {
        const params: any = {
          capitalisation_status_in: "Pending,OnHold",
          status__not_in: "Disposed,Condemned",
          ordering: "-created_at",
          limit: 200,
        };
        if (branchId) params.branch = branchId;

        type Raw = Omit<CapitalisationRow, "department_name"> & {
          department: { name: string } | null;
          capitalised_value?: number;
          purchase_cost?: number;
        };
        const res = await apiList<Raw>("/api/v1/assets/", params);

        return (res.results || []).map((r) => ({
          id: r.id,
          asset_code: r.asset_code,
          name: r.name,
          basic_cost: r.basic_cost ?? (r as any).purchase_cost ?? null,
          total_capitalised_value: r.total_capitalised_value ?? (r as any).capitalised_value ?? null,
          capitalisation_date: r.capitalisation_date,
          capitalisation_status: r.capitalisation_status || "Pending",
          capitalisation_remarks: r.capitalisation_remarks ?? null,
          department_name: r.department?.name ?? null,
        })) as CapitalisationRow[];
      } catch {
        return [];
      }
    },
  });
}

function useCapitalisedRows(branchId: string | null) {
  return useQuery({
    queryKey: ["capitalisation-done", branchId ?? null],
    queryFn: async () => {
      try {
        const params: any = {
          capitalisation_status_in: "Capitalised",
          status__not_in: "Disposed,Condemned",
          ordering: "-updated_at",
          limit: 200,
        };
        if (branchId) params.branch = branchId;

        type Raw = Omit<CapitalisationRow, "department_name"> & {
          department: { name: string } | null;
          capitalised_value?: number;
          purchase_cost?: number;
        };
        const res = await apiList<Raw>("/api/v1/assets/", params);

        return (res.results || []).map((r) => ({
          id: r.id,
          asset_code: r.asset_code,
          name: r.name,
          basic_cost: r.basic_cost ?? (r as any).purchase_cost ?? null,
          total_capitalised_value: r.total_capitalised_value ?? (r as any).capitalised_value ?? null,
          capitalisation_date: r.capitalisation_date,
          capitalisation_status: r.capitalisation_status || "Capitalised",
          capitalisation_remarks: r.capitalisation_remarks ?? null,
          department_name: r.department?.name ?? null,
        })) as CapitalisationRow[];
      } catch {
        return [];
      }
    },
  });
}

function useCapitaliseAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      id: string;
      capitalised_value: number;
      capitalisation_date: string;
      capitalisation_remarks: string | null;
      status: "Capitalised" | "OnHold" | "Rejected";
    }) => {
      await apiPatch(`/api/v1/assets/${v.id}/`, {
        capitalised_value: v.capitalised_value,
        total_capitalised_value: v.capitalised_value,
        capitalisation_date: v.capitalisation_date,
        capitalisation_remarks: v.capitalisation_remarks,
        capitalisation_status: v.status,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["capitalisation-pending"] });
      qc.invalidateQueries({ queryKey: ["capitalisation-done"] });
      qc.invalidateQueries({ queryKey: ["assets"] });
      qc.invalidateQueries({ queryKey: ["far"] });
      qc.invalidateQueries({ queryKey: ["pending-capitalisation"] });
      qc.invalidateQueries({ queryKey: ["finance-kpis"] });
    },
  });
}

function CapitalisationPage() {
  const { data: me } = useCurrentUser();
  const { currentBranchId } = useBranch();
  const canCapitalise = canRunDepreciation(me?.roles ?? []);
  const [activeTab, setActiveTab] = useState<string>("pending");

  const { data: pendingRows = [], isLoading: isPendingLoading } = usePendingRows(currentBranchId);
  const { data: capitalisedRows = [], isLoading: isCapitalisedLoading } = useCapitalisedRows(currentBranchId);

  const [pendingSearch, setPendingSearch] = useState("");
  const [capSearch, setCapSearch] = useState("");

  const [edit, setEdit] = useState<CapitalisationRow | null>(null);
  const [value, setValue] = useState("");
  const [date, setDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [remarks, setRemarks] = useState("");
  const mut = useCapitaliseAsset();

  const filteredPending = useMemo(() => {
    if (!pendingSearch.trim()) return pendingRows;
    const q = pendingSearch.toLowerCase();
    return pendingRows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.asset_code.toLowerCase().includes(q) ||
        (r.department_name && r.department_name.toLowerCase().includes(q))
    );
  }, [pendingRows, pendingSearch]);

  const filteredCapitalised = useMemo(() => {
    if (!capSearch.trim()) return capitalisedRows;
    const q = capSearch.toLowerCase();
    return capitalisedRows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.asset_code.toLowerCase().includes(q) ||
        (r.department_name && r.department_name.toLowerCase().includes(q))
    );
  }, [capitalisedRows, capSearch]);

  function open(r: CapitalisationRow) {
    setEdit(r);
    setValue(String(r.total_capitalised_value ?? r.basic_cost ?? 0));
    setDate(r.capitalisation_date ?? new Date().toISOString().slice(0, 10));
    setRemarks(r.capitalisation_remarks ?? "");
  }

  async function submit(status: "Capitalised" | "OnHold" | "Rejected") {
    if (!edit) return;
    const val = Number(value);
    if (status === "Capitalised" && (!val || val <= 0)) {
      toast.error("Capitalised value is required");
      return;
    }
    try {
      await mut.mutateAsync({
        id: edit.id,
        capitalised_value: val,
        capitalisation_date: date,
        capitalisation_remarks: remarks || null,
        status,
      });
      if (status === "Capitalised") {
        toast.success(`Asset "${edit.asset_code}" marked as Capitalised`, {
          action: {
            label: "View Capitalised",
            onClick: () => setActiveTab("capitalised"),
          },
        });
      } else {
        toast.success(`Status updated to ${status}`);
      }
      setEdit(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update capitalisation");
    }
  }

  return (
    <div className="space-y-5">
      {/* Back navigation & Page header */}
      <div>
        <Button
          variant="ghost"
          size="sm"
          asChild
          className="mb-2 -ml-2 h-8 gap-1.5 text-muted-foreground hover:text-foreground"
        >
          <Link to="/finance/dashboard">
            <ArrowLeft className="h-4 w-4" />
            Back to Finance
          </Link>
        </Button>
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Finance Capitalisation</h1>
            <p className="text-sm text-muted-foreground">
              Review pending assets, confirm capitalised value and date, and monitor all capitalised assets.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="h-10 p-1">
          <TabsTrigger value="pending" className="gap-2 px-3 py-1.5">
            <Clock className="h-4 w-4 text-amber-500" />
            <span>Pending Capitalisation</span>
            <Badge variant="secondary" className="ml-1 px-1.5 py-0.5 text-xs font-semibold">
              {pendingRows.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="capitalised" className="gap-2 px-3 py-1.5">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <span>Capitalised Assets</span>
            <Badge variant="secondary" className="ml-1 px-1.5 py-0.5 text-xs font-semibold">
              {capitalisedRows.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="both" className="gap-2 px-3 py-1.5">
            <Layers className="h-4 w-4 text-muted-foreground" />
            <span>View Both Sections</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab: Pending Only */}
        <TabsContent value="pending" className="mt-0">
          {renderPendingCard()}
        </TabsContent>

        {/* Tab: Capitalised Only */}
        <TabsContent value="capitalised" className="mt-0">
          {renderCapitalisedCard()}
        </TabsContent>

        {/* Tab: Both Sections */}
        <TabsContent value="both" className="mt-0 space-y-6">
          {renderPendingCard()}
          {renderCapitalisedCard()}
        </TabsContent>
      </Tabs>

      {/* Capitalisation Dialog Modal */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {edit?.capitalisation_status === "Capitalised"
                ? "Edit Capitalisation Details"
                : "Capitalise Asset"}
            </DialogTitle>
          </DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div className="rounded-md border bg-muted/40 p-3 text-sm">
                <div className="font-medium text-foreground">{edit.name}</div>
                <div className="text-xs text-muted-foreground">Asset Code: {edit.asset_code}</div>
                {edit.department_name && (
                  <div className="text-xs text-muted-foreground">Department: {edit.department_name}</div>
                )}
                {edit.basic_cost != null && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    Basic Cost: <span className="font-semibold text-foreground">{inr(edit.basic_cost)}</span>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Capitalised Value (₹) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="Enter value"
                  />
                </div>
                <div>
                  <Label>Capitalisation Date *</Label>
                  <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                </div>
              </div>
              <div>
                <Label>Remarks</Label>
                <Textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  rows={3}
                  placeholder="Enter remarks, approval details, or reasons..."
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => submit("Rejected")} disabled={mut.isPending}>
              Reject
            </Button>
            <Button variant="outline" onClick={() => submit("OnHold")} disabled={mut.isPending}>
              On Hold
            </Button>
            <Button onClick={() => submit("Capitalised")} disabled={mut.isPending}>
              {edit?.capitalisation_status === "Capitalised" ? "Update Details" : "Mark Capitalised"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  function renderPendingCard() {
    return (
      <Card>
        <CardHeader className="flex flex-col gap-3 pb-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-base font-semibold">Pending Capitalisation Queue</CardTitle>
            <CardDescription>
              Assets received and installed awaiting finance approval. Depreciation runs only after an asset is Capitalised.
            </CardDescription>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search pending assets..."
              value={pendingSearch}
              onChange={(e) => setPendingSearch(e.target.value)}
              className="pl-8 text-sm"
            />
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Asset</TableHead>
                <TableHead>Department</TableHead>
                <TableHead className="text-right">Basic Cost</TableHead>
                <TableHead className="text-right">Capitalised Value</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Remarks</TableHead>
                <TableHead className="w-28 text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isPendingLoading && (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                    Loading pending assets…
                  </TableCell>
                </TableRow>
              )}
              {!isPendingLoading && filteredPending.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                    {pendingSearch.trim() ? "No matching pending assets found." : "No pending assets in the queue."}
                  </TableCell>
                </TableRow>
              )}
              {filteredPending.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <div className="font-medium">{r.name}</div>
                    <div className="text-xs text-muted-foreground">{r.asset_code}</div>
                  </TableCell>
                  <TableCell>{r.department_name ?? "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.basic_cost != null ? inr(r.basic_cost) : "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.total_capitalised_value != null ? inr(r.total_capitalised_value) : "—"}
                  </TableCell>
                  <TableCell>
                    <Badge variant={r.capitalisation_status === "OnHold" ? "secondary" : "outline"}>
                      {r.capitalisation_status}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground" title={r.capitalisation_remarks ?? ""}>
                    {r.capitalisation_remarks ?? "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!canCapitalise}
                      onClick={() => open(r)}
                    >
                      Review
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  }

  function renderCapitalisedCard() {
    return (
      <Card>
        <CardHeader className="flex flex-col gap-3 pb-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-base font-semibold">Capitalised Assets</CardTitle>
            <CardDescription>
              Assets with confirmed capitalised value and date active in the Fixed Asset Register.
            </CardDescription>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search capitalised assets..."
              value={capSearch}
              onChange={(e) => setCapSearch(e.target.value)}
              className="pl-8 text-sm"
            />
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Asset</TableHead>
                <TableHead>Department</TableHead>
                <TableHead className="text-right">Basic Cost</TableHead>
                <TableHead className="text-right">Capitalised Value</TableHead>
                <TableHead>Cap. Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Remarks</TableHead>
                <TableHead className="w-36 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isCapitalisedLoading && (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">
                    Loading capitalised assets…
                  </TableCell>
                </TableRow>
              )}
              {!isCapitalisedLoading && filteredCapitalised.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">
                    {capSearch.trim() ? "No matching capitalised assets found." : "No capitalised assets yet."}
                  </TableCell>
                </TableRow>
              )}
              {filteredCapitalised.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <div className="font-medium">{r.name}</div>
                    <div className="text-xs text-muted-foreground">{r.asset_code}</div>
                  </TableCell>
                  <TableCell>{r.department_name ?? "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.basic_cost != null ? inr(r.basic_cost) : "—"}
                  </TableCell>
                  <TableCell className="text-right font-medium tabular-nums text-foreground">
                    {r.total_capitalised_value != null ? inr(r.total_capitalised_value) : "—"}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {r.capitalisation_date ?? "—"}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="border-emerald-500/40 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
                      Capitalised
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-[180px] truncate text-sm text-muted-foreground" title={r.capitalisation_remarks ?? ""}>
                    {r.capitalisation_remarks ?? "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 px-2 text-xs"
                        asChild
                        title="View Asset Details"
                      >
                        <Link to="/assets/$assetId" params={{ assetId: r.id }}>
                          <ExternalLink className="mr-1 h-3.5 w-3.5" />
                          View
                        </Link>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 px-2 text-xs"
                        disabled={!canCapitalise}
                        onClick={() => open(r)}
                        title="Edit Capitalisation Details"
                      >
                        <Pencil className="mr-1 h-3.5 w-3.5" />
                        Edit
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  }
}
